import { Switch } from '@/components'
import { cn } from '@/lib/utils'
import type { PropsWithChildren, ReactNode } from 'react'
import { useId } from 'react'

export const SettingsSection: React.FC<
  PropsWithChildren<{ title: ReactNode; description?: ReactNode }>
> = ({ title, description, children }) => (
  <section className="overflow-hidden rounded-2xl bg-white shadow-card">
    <header className="border-b border-stone-200 px-5 py-4 sm:px-6">
      <h3 className="text-lg font-semibold text-stone-900">{title}</h3>
      {description && (
        <p className="mt-1 text-sm text-stone-500">{description}</p>
      )}
    </header>
    <div className="divide-y divide-stone-200">{children}</div>
  </section>
)

const RowText: React.FC<{
  label: ReactNode
  description?: ReactNode
  htmlFor?: string
}> = ({ label, description, htmlFor }) => (
  <div className="space-y-1">
    <label
      htmlFor={htmlFor}
      className="block text-sm font-semibold text-stone-900"
    >
      {label}
    </label>
    {description && <p className="text-xs text-stone-500">{description}</p>}
  </div>
)

/** A setting with its label and explanation on the left and the input on the right. */
export const SettingRow: React.FC<
  PropsWithChildren<{
    label: ReactNode
    description?: ReactNode
    error?: string | null
    className?: string
  }>
> = ({ label, description, error, className, children }) => (
  <div
    className={cn(
      'grid gap-3 px-5 py-4 sm:px-6 md:grid-cols-[minmax(0,2fr)_minmax(0,3fr)] md:items-center md:gap-8',
      className
    )}
  >
    <RowText label={label} description={description} />
    <div className="min-w-0">
      {children}
      {error && <p className="mt-1 ml-1 text-xs text-red-600">{error}</p>}
    </div>
  </div>
)

export const SettingSwitch: React.FC<{
  label: ReactNode
  description?: ReactNode
  checked: boolean
  disabled?: boolean
  onChange(checked: boolean): void
}> = ({ label, description, checked, disabled, onChange }) => {
  const id = useId()
  return (
    <div className="flex items-start justify-between gap-6 px-5 py-4 sm:px-6">
      <RowText label={label} description={description} htmlFor={id} />
      <Switch
        id={id}
        className="mt-0.5"
        checked={checked}
        disabled={disabled}
        onCheckedChange={onChange}
      />
    </div>
  )
}

export const SettingNote: React.FC<
  PropsWithChildren<{ tone?: 'info' | 'warning' }>
> = ({ tone = 'info', children }) => (
  <div
    className={cn(
      'px-5 py-3 text-sm sm:px-6',
      tone === 'info' && 'bg-stone-50 text-stone-600',
      tone === 'warning' && 'bg-amber-50 text-amber-900'
    )}
  >
    {children}
  </div>
)
